import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { Button, Tag, Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsClientClientResponseSummaryModel } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useGetClients } from 'src/hooks/api/useClientsEndpoint';
import { useAppNavigation } from 'src/routes';

import { EditOutlined } from '@ant-design/icons';

import { StyledSpace } from '@/pages/Users/components/UsersTable/UsersTable.styled';

import {
  ADMIN_CLIENT_STATUS_FILTER_OPTIONS,
  AdminClientStatusFilterDropdown,
} from './components/AdminClientStatusFilterDropdown/AdminClientStatusFilterDropdown';
import {
  AdminClientsMobileActionsColumn,
  AdminClientsMobileNameColumn,
} from './components/AdminClientsMobileRow/AdminClientsMobileRow';

const isSuspendedStatus = (status: string | null | undefined): boolean => {
  const s = status?.toLowerCase() ?? '';
  return s.includes('suspend');
};

const rowMatchesStatusFilterKeys = (
  record: FanguardApiContractsClientClientResponseSummaryModel,
  selectedKeys: string[],
) => {
  if (!selectedKeys.length) {
    return true;
  }
  const suspended = isSuspendedStatus(record.status);
  return selectedKeys.some((key) => {
    if (key === 'active') {
      return !suspended;
    }
    if (key === 'suspended') {
      return suspended;
    }
    return true;
  });
};

interface AdminClientsContextProps {
  isLoading: boolean;
  clients: FanguardApiContractsClientClientResponseSummaryModel[];
  tableColumns: ColumnType<FanguardApiContractsClientClientResponseSummaryModel>[];
  totalItems: number;
  filterBySearch: (value: string) => void;
}

const AdminClientsContext = createContext<AdminClientsContextProps | undefined>(undefined);

export const useAdminClients = (): AdminClientsContextProps => {
  const context = useContext(AdminClientsContext);
  if (!context) {
    throw new Error('useAdminClients must be used within AdminClientsProvider');
  }
  return context;
};

export const AdminClientsProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { navigateTo } = useAppNavigation();
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeClientId, setActiveClientId] = useState<string | null>(null);
  const { data, isLoading } = useGetClients();

  const filterBySearch = useCallback((value: string) => {
    setSearchTerm(value.trim());
  }, []);

  const filteredClients = useMemo(() => {
    const afterStatus = data?.filter((record) => rowMatchesStatusFilterKeys(record, selectedStatusFilters)) ?? [];
    if (!searchTerm) {
      return afterStatus;
    }
    const query = searchTerm.toLowerCase();
    return afterStatus.filter((record) => {
      const name = record.name?.toLowerCase() ?? '';
      const id = record.id?.toLowerCase() ?? '';
      return name.includes(query) || id.includes(query);
    });
  }, [data, selectedStatusFilters, searchTerm]);

  const totalItems = filteredClients.length;

  const statusFilterColumnConfig = useMemo<
    Pick<
      ColumnType<FanguardApiContractsClientClientResponseSummaryModel>,
      'filters' | 'filteredValue' | 'filterMultiple' | 'onFilter' | 'filterDropdown'
    >
  >(
    () => ({
      filters: ADMIN_CLIENT_STATUS_FILTER_OPTIONS.map((option) => ({
        text: t(`adminClients.filters.${option}`),
        value: option,
      })),
      filteredValue: selectedStatusFilters.length ? selectedStatusFilters : null,
      filterMultiple: false,
      onFilter: (value, record) => rowMatchesStatusFilterKeys(record, [String(value)]),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const clearFiltersFn = clearFilters ?? (() => {});

        return (
          <AdminClientStatusFilterDropdown
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFiltersFn}
            setSelectedFilters={setSelectedStatusFilters}
          />
        );
      },
    }),
    [selectedStatusFilters, t],
  );

  const onEditClient = useCallback(
    (record: FanguardApiContractsClientClientResponseSummaryModel) => {
      if (record.id) {
        navigateTo.adminClientsDetails(record.id);
      }
    },
    [navigateTo],
  );

  const desktopColumns: ColumnType<FanguardApiContractsClientClientResponseSummaryModel>[] = useMemo(
    () => [
      {
        title: t('adminClients.table.id'),
        dataIndex: 'id',
        key: 'id',
        width: 120,
        ellipsis: true,
        render: (value: string | null | undefined) => (
          <Tooltip title={value ?? ''}>
            <Typography.Text ellipsis>{value ?? '—'}</Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: t('adminClients.table.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (value: string | null | undefined) => (
          <Tooltip title={value ?? ''}>
            <Typography.Text ellipsis>{value ?? '—'}</Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: t('adminClients.table.status'),
        dataIndex: 'status',
        key: 'status',
        width: 177,
        ...statusFilterColumnConfig,
        render: (value: string | null | undefined) => {
          if (!value) {
            return '—';
          }
          const suspended = isSuspendedStatus(value);
          return <Tag color={suspended ? 'error' : 'success'}>{value}</Tag>;
        },
      },
      {
        title: t('adminClients.table.licensedFor'),
        dataIndex: 'licensedFor',
        key: 'licensedFor',
        render: (_value, record) => {
          if (!record.licensedFor?.length) {
            return '—';
          }
          return record.licensedFor
            ?.map((key) => t(`adminClients.licensedFor.${key}`, { defaultValue: key }))
            .join(', ');
        },
      },
      {
        title: t('adminClients.table.actions'),
        key: 'actions',
        width: 100,
        align: 'right',
        render: (_value, record) => (
          <StyledSpace size={8}>
            <Tooltip title={t('adminClients.table.edit')}>
              <Button type="text" icon={<EditOutlined />} onClick={() => onEditClient(record)} />
            </Tooltip>
          </StyledSpace>
        ),
      },
    ],
    [t, onEditClient, statusFilterColumnConfig],
  );

  const mobileColumns: ColumnType<FanguardApiContractsClientClientResponseSummaryModel>[] = useMemo(
    () => [
      {
        title: t('adminClients.table.name'),
        dataIndex: 'name',
        key: 'name',
        render: (_value, record) => (
          <AdminClientsMobileNameColumn
            client={record}
            activeClientId={activeClientId}
            setActiveClientId={setActiveClientId}
          />
        ),
      },
      {
        title: t('adminClients.table.actions'),
        key: 'actions',
        align: 'right',
        render: (_value, record) => (
          <AdminClientsMobileActionsColumn
            client={record}
            activeClientId={activeClientId}
            onEditClient={onEditClient}
          />
        ),
      },
    ],
    [t, activeClientId, onEditClient],
  );

  const tableColumns = isDesktop ? desktopColumns : mobileColumns;

  const contextValue = useMemo(
    () => ({
      isLoading,
      clients: filteredClients,
      tableColumns,
      totalItems,
      filterBySearch,
    }),
    [isLoading, filteredClients, tableColumns, totalItems, filterBySearch],
  );

  return <AdminClientsContext.Provider value={contextValue}>{children}</AdminClientsContext.Provider>;
};
